import type { AppType } from '@mixtape/api';
import { isApiErrorBody, type ErrorCode } from '@mixtape/shared';
import { hc, type ClientResponse, type parseResponse } from 'hono/client';

/** Typed RPC client for the Mixtapes API (same origin, cookies included). */
export const api = hc<AppType>('/', { init: { credentials: 'same-origin' } });

export class ApiClientError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number;
  readonly details: Record<string, string[]> | undefined;

  constructor(code: string, message: string, status: number, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export async function toApiError(res: Response): Promise<ApiClientError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // Not JSON (e.g. proxy error page).
  }
  if (isApiErrorBody(body)) {
    return new ApiClientError(body.error.code, body.error.message, res.status, body.error.details);
  }
  // Better Auth errors use { code, message }.
  const flat = body as { code?: unknown; message?: unknown } | null;
  if (flat && typeof flat.code === 'string') {
    return new ApiClientError(flat.code, String(flat.message ?? flat.code), res.status);
  }
  return new ApiClientError(
    res.status >= 500 ? 'INTERNAL' : 'BAD_REQUEST',
    res.statusText,
    res.status,
  );
}

type Parsed<T extends ClientResponse<unknown>> = Awaited<ReturnType<typeof parseResponse<T>>>;

/** Await a typed client call; throws ApiClientError for non-2xx responses. */
export async function call<T extends ClientResponse<unknown>>(
  req: T | Promise<T>,
): Promise<Parsed<T>> {
  let res: T;
  try {
    res = await req;
  } catch {
    throw new ApiClientError('NETWORK', 'Network error', 0);
  }
  if (!res.ok) throw await toApiError(res as unknown as Response);
  return (await res.json()) as Parsed<T>;
}

/** Plain JSON POST for endpoints outside the typed client (Better Auth). */
export async function postJson<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiClientError('NETWORK', 'Network error', 0);
  }
  if (!res.ok) throw await toApiError(res);
  const text = await res.text();
  return (text ? JSON.parse(text) : {}) as T;
}
